import React, { useState, useRef } from "react";
import "./Login.css";
import "./VerifyEmail.css";
import GradientButton from "../../components/button/Button";
import { Logo } from "../../images/Images";
import LoadingComponent from "../../components/loading/Loading";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";

const VerifyEmail: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Function to handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move to the next input field
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  // Function to handle backspace
  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Function to handle form submission
  const handleVerify = async () => {
    const identifier = localStorage.getItem("verify_id");
    const otpCode = otp.join("");

    const payload = {
      identifier,
      token: otpCode,
    };

    try {
      setLoading(true);
      const res = await axiosInstance.post("/auth/verify-account", payload);
      setLoading(false);

      // Check for a valid success flag or status
      if (
        res.data &&
        (res.data.valid || res.data.success || res.data.status === "success")
      ) {
        toast.success("Email verified successfully. Please login now.");
        localStorage.removeItem("verify_id");
        localStorage.setItem("verify_status", "true");
        setTimeout(() => {
          window.location.href = "/login";
        }, 3000);
      } else {
        toast.error(
          res.data?.message || "Verification failed. Please try again."
        );
      }
    } catch (error: any) {
      setLoading(false);
      toast.error(error?.response?.data?.message || "Error verifying token");
    }
  };

  // Add this function inside your VerifyEmail component:
  const handleResendCode = async () => {
    const email = localStorage.getItem("verify_username");
    if (!email) {
      toast.error("No email found to resend code.");
      return;
    }
    try {
      setLoading(true);
      await axiosInstance.post("/auth/resend-token", { identifier: email });
      toast.success("OTP resent to your email.");

      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      toast.error(
        error?.response?.data?.message ||
          "Failed to resend OTP. Please try again."
      );
    }
  };

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <div className="login-page">
      <div className="login-page__image">
        <img
          src="https://img.freepik.com/free-photo/top-view-vegetables-fruits-zucchini-bell-peppers-apples-quince-cherry-tomatoes-cumcuat-parsley-cabbage-lemon-pomegranates_140725-59438.jpg?uid=R27806234&ga=GA1.1.1852762410.1725342281&semt=ais_hybrid"
          alt="Fresh Produce"
        />
      </div>

      <div className="login-page__form-container verify-form_container">
        <form
          className="login-page__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleVerify();
          }}
        >
          <div className="verify-email-page__title">
            <img src={Logo} alt="logo" className="verify-email-page__logo" />
          </div>
          <h2 className="verify-email-page__subtitle">
            Verify your Email Address
          </h2>
          <p className="verify-email-page__description">
            Enter the OTP sent to your Email Address
          </p>

          {/* OTP Input Fields */}
          <div className="verify-email-page__otp-container">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                maxLength={1}
                className="verify-email-page__otp-input"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                ref={(el) => (inputRefs.current[index] = el)}
              />
            ))}
          </div>

          {/* Verify Button */}
          <div className="verify-email-page__button-container">
            <GradientButton
              text="Verify Email Address"
              onClick={handleVerify}
            />
          </div>

          {/* Resend Code */}
          <p className="verify-email-page__resend">
            Didn’t get an OTP?{" "}
            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                color: "#089B14",
                cursor: "pointer",
                textDecoration: "underline",
                padding: 0,
              }}
              onClick={handleResendCode}
            >
              Resend Code
            </button>
          </p>

          {/* Back to Login link */}
          <p className="verify-email-page__back-login">
            <a
              href="/login"
              style={{ color: "#089B14", textDecoration: "underline" }}
            >
              &larr; Back to Login
            </a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default VerifyEmail;
